import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ProtectedRoute from '@/components/ProtectedRoute';
import LanguageOnboarding from '@/components/LanguageOnboarding';
import '@/lib/i18n';

// Page imports
import Dashboard from '@/pages/Dashboard';
import ImcCalculator from '@/pages/ImcCalculator';
import PhotoAnalysis from '@/pages/PhotoAnalysis';
import Progress from '@/pages/Progress';
import Treatments from '@/pages/Treatments';
import EvaluationHistory from '@/pages/EvaluationHistory';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Setup from '@/pages/Setup';
import ResendSetup from '@/pages/ResendSetup';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Account from '@/pages/Account';
import Admin from '@/pages/Admin';
import DownloadApp from '@/pages/DownloadApp';
import WebGate from '@/components/WebGate';
import { isNative } from '@/lib/native';

const AuthenticatedApp = () => {
  const { isLoadingAuth } = useAuth();

  if (isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-secondary border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={isNative() ? <Register /> : <DownloadApp />} />
      <Route path="/setup" element={<Setup />} />
      <Route path="/resend-setup" element={<ResendSetup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
        <Route element={<WebGate />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/account" element={<Account />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/imc" element={<ImcCalculator />} />
          <Route path="/photos" element={<PhotoAnalysis />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/treatments" element={<Treatments />} />
          <Route path="/history" element={<EvaluationHistory />} />
        </Route>
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <AuthenticatedApp />
          <LanguageOnboarding />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App